/**
 * Audit viewer (§8.4): payroll_audit_log, newest first, filter by entity
 * type / action / editor, expandable before→after JSON, load-more paging.
 */

import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Collapse,
  Flex,
  Input,
  Select,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { payrollApi } from '../api';

const ENTITY_TYPES = ['lo_profile', 'lo_profile_version', 'lender', 'margin_version', 'lender_alias',
  'split_rule', 'referral_rule', 'loa_fee', 'email_alias', 'engine_config', 'reference_import',
  'payroll_lo_profile_versions', 'pipeline_utility'];
const ACTIONS = ['create', 'update', 'delete', 'import_commit', 'config_change', 'live_run', 'backfill', 'setup'];

const ACTION_COLORS = {
  create: 'green', update: 'blue', delete: 'red', import_commit: 'purple',
  config_change: 'red', live_run: 'orange', backfill: 'gray', setup: 'gray',
};

function JsonBlock({ label, value }) {
  if (value === null || value === undefined) return null;
  return (
    <Box mt={1}>
      <Text fontSize="xs" fontWeight="semibold" color="gray.500">{label}</Text>
      <Code display="block" whiteSpace="pre-wrap" fontSize="xs" p={2} borderRadius="md" maxH="260px" overflowY="auto">
        {JSON.stringify(value, null, 2)}
      </Code>
    </Box>
  );
}

function AuditRow({ row }) {
  const [open, setOpen] = useState(false);
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  return (
    <Box borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
      <Flex px={2} py={1.5} gap={2} align="baseline" cursor="pointer" _hover={{ bg: hoverBg }}
        onClick={() => setOpen((o) => !o)} wrap="wrap">
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" minW="150px">
          {new Date(row.created_at).toLocaleString()}
        </Text>
        <Badge colorScheme={ACTION_COLORS[row.action] || 'gray'}>{row.action}</Badge>
        <Badge variant="outline">{row.entity_type}</Badge>
        <Text fontSize="sm" flex="1" minW="180px">
          {row.reason || <Text as="span" color="gray.500">(no reason)</Text>}
        </Text>
        <Text fontSize="xs" color="gray.500">{row.changed_by_email || row.changed_by_uid || 'system'}</Text>
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box px={4} pb={3}>
          {row.entity_id && <Text fontSize="xs" color="gray.500">entity_id: <Code fontSize="xs">{row.entity_id}</Code></Text>}
          <JsonBlock label="Before" value={row.before} />
          <JsonBlock label="After" value={row.after} />
        </Box>
      </Collapse>
    </Box>
  );
}

export default function PayrollAuditViewer() {
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [filters, setFilters] = useState({ entityType: '', action: '', changedBy: '' });

  const buildQuery = (extra) => {
    const q = { limit: 100, ...extra };
    if (filters.entityType) q.entityType = filters.entityType;
    if (filters.action) q.action = filters.action;
    if (filters.changedBy.trim()) q.changedBy = filters.changedBy.trim();
    return q;
  };

  const load = async () => {
    setBusy(true);
    setLoadError(null);
    try {
      const { rows: data } = await payrollApi.listAudit(buildQuery());
      setRows(data);
      setExhausted(data.length < 100);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadMore = async () => {
    if (!rows || !rows.length) return;
    setBusy(true);
    try {
      const { rows: more } = await payrollApi.listAudit(buildQuery({ before: rows[rows.length - 1].created_at }));
      setRows((r) => [...r, ...more]);
      setExhausted(more.length < 100);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <Box>
      <Flex gap={2} mb={3} wrap="wrap">
        <Select size="sm" maxW="200px" placeholder="All entity types" value={filters.entityType}
          onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value }))}>
          {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
        <Select size="sm" maxW="180px" placeholder="All actions" value={filters.action}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}>
          {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </Select>
        <Input size="sm" maxW="220px" placeholder="Editor email contains…" value={filters.changedBy}
          onChange={(e) => setFilters((f) => ({ ...f, changedBy: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
        <Button size="sm" onClick={load} isLoading={busy && !rows}>Apply</Button>
      </Flex>

      {loadError && <Alert status="error" borderRadius="md" mb={3}><AlertIcon />{loadError}</Alert>}
      {!rows && !loadError && <Flex justify="center" py={10}><Spinner /></Flex>}

      {rows && (
        <Box borderWidth="1px" borderRadius="md">
          {rows.length === 0 && <Text p={4} fontSize="sm" color="gray.500">No audit entries match.</Text>}
          {rows.map((r) => <AuditRow key={r.id} row={r} />)}
        </Box>
      )}

      {rows && rows.length > 0 && !exhausted && (
        <Flex justify="center" mt={3}>
          <Button size="sm" variant="outline" onClick={loadMore} isLoading={busy}>Load more</Button>
        </Flex>
      )}
    </Box>
  );
}
